import { JSX, Show } from 'solid-js';
import clsx from 'clsx';
import { ChevronDownIcon } from '../../components/icons-library/chevron-down';

type DropdownProps = {
    id?: string;
    label?: string;
    placeholder?: string;
    expanded?: boolean;
    disabled?: boolean;
    onToggle?: (event: MouseEvent) => void;
    'aria-controls'?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    class?: string;
    children?: JSX.Element;
};

/**
 * Closed-state DS dropdown trigger (Figma 173:10907).
 * Renders the label + chevron pill; consumer owns the panel and wires
 * `expanded` + `onToggle`. Children render below the trigger when
 * `expanded` is true, so the same component covers both menu-list and
 * accordion-disclosure use cases.
 */
export const Dropdown = (props: DropdownProps) => {
    return (
        <div class={clsx('w-full', props.class)}>
            <button
                type="button"
                id={props.id}
                class={clsx(
                    'flex w-full items-center gap-cvi-3',
                    'px-cvi-5 py-cvi-5',
                    'rounded-cvi-md bg-cvi-bg-neutral-subtle',
                    'label-lg font-cvi-regular text-cvi-fg-muted text-left',
                    'cursor-pointer appearance-none',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
                aria-expanded={props.expanded ? 'true' : 'false'}
                aria-controls={props['aria-controls']}
                aria-label={props['aria-label']}
                aria-labelledby={props['aria-labelledby']}
                disabled={props.disabled}
                onClick={props.onToggle}
            >
                <span class="flex-1 min-w-0 truncate">
                    {props.label || props.placeholder}
                </span>
                <ChevronDownIcon
                    class={clsx(
                        'shrink-0 text-cvi-fg-default transition-transform',
                        props.expanded && 'rotate-180',
                    )}
                    ariaHidden={true}
                />
            </button>
            <Show when={props.expanded && props.children}>
                {props.children}
            </Show>
        </div>
    );
};
